import api, { hardRedirect } from './api';

// Idle timeout configuration (5 minutes)
const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
let idleTimer = null;

// Initialize idle timeout tracking
const initIdleTimeout = () => {
  clearIdleTimeout();
  
  idleTimer = setTimeout(() => {
    console.log('Session expired due to inactivity');
    logout();
  }, IDLE_TIMEOUT);
};

const clearIdleTimeout = () => {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
};

// Reset idle timer on user activity
export const resetIdleTimer = () => {
  if (isAuthenticated()) {
    initIdleTimeout();
  }
};

// The single writer of the session (P8-01). `login` and `verify2fa` receive the
// identical enriched AuthResponse from the backend, and previously each built the
// session object from its own literal — which had already drifted (one read
// `agencyId` off the destructure, the other off `response.data`). Both now share
// this, so the tenant dimension can never be persisted on one path only.
//
// `tenant` is the JWT claim value ("master" or the decimal agency id) and is what
// the route guards and Sidebar switch on; `agencyName` is the display name, the
// only producer a non-admin session has for it.
/**
 * The stored session's shape version (ORG-08, #303).
 *
 * ORG-08 replaced the `orgGroupId` / `orgGroupPath` pair with a `primaryGroup`
 * object, so a session written by the previous release describes a model that no longer
 * exists. Rather than tolerate both shapes forever, the readers below reject anything
 * that is not this version and clear it — so on the first navigation after a deploy every
 * open tab is signed out ONCE and nobody sees a half-working UI.
 *
 * This is the ONLY session bump in the whole ORG epic. The shape is forward-tolerant
 * (unknown extra fields are ignored), so no later issue needs another one.
 */
export const SESSION_SHAPE_VERSION = 2;

const persistSession = (data) => {
  sessionStorage.setItem('token', data.token);
  sessionStorage.setItem('user', JSON.stringify({
    username: data.username,
    role: data.role,
    // {id, name, typeLabel, path} or null. DISPLAY ONLY -- nothing gates on it here;
    // anything that decides something reads /api/users/me.
    primaryGroup: data.primaryGroup ?? null,
    agencyId: data.agencyId,
    tenant: data.tenant,
    agencyName: data.agencyName,
    sessionVersion: SESSION_SHAPE_VERSION,
  }));
  initIdleTimeout();
};

/**
 * A sign-in answer that is not a session yet, in the shape the sign-in screen works from —
 * or null when the answer IS a session.
 *
 * In both cases `token` is a credential for the next step only. Neither may reach
 * `persistSession`: the request interceptor would send it as a bearer on every call, and
 * the server refuses both there.
 *
 *  - `twoFactorRequired`: the temp token `verify2fa` exchanges for the next answer.
 *  - `passwordChangeRequired`: the sign-in used an emailed one-time password. The token is
 *    a password-change grant, good only for `completePasswordReset`, which is what answers
 *    with the session.
 */
const unfinishedSignIn = (data) => {
  if (data?.twoFactorRequired) {
    return { twoFactorRequired: true, tempToken: data.token, username: data.username };
  }
  if (data?.passwordChangeRequired) {
    return { passwordChangeRequired: true, changeToken: data.token, username: data.username };
  }
  return null;
};

export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });

  const unfinished = unfinishedSignIn(response.data);
  if (unfinished) return unfinished;

  persistSession(response.data);

  return response.data;
};

export const logout = () => {
  clearIdleTimeout();
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  window.location.href = '/login';
};

/**
 * The stored session, or null.
 *
 * A session whose `sessionVersion` is not the current one is treated as ABSENT and
 * cleared — the same strictness `isMasterContext()` already applies to a missing
 * `tenant`. A stale session describes a data model the app no longer has, and rendering
 * from it produces a screen that looks logged in and fails on the first request.
 */
export const getCurrentUser = () => {
  const userStr = sessionStorage.getItem('user');
  if (!userStr) return null;
  let parsed;
  try {
    parsed = JSON.parse(userStr);
  } catch {
    // Corrupt JSON is indistinguishable from a hostile value; drop it.
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    return null;
  }
  if (parsed?.sessionVersion !== SESSION_SHAPE_VERSION) {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    return null;
  }
  return parsed;
};

export const isAuthenticated = () => {
  return !!sessionStorage.getItem('token');
};

// ---------------------------------------------------------------------------
// Tenancy (P8-01)
// ---------------------------------------------------------------------------

/** The bound tenant claim: 'master', a decimal agency id string, or null. */
export const getTenant = () => getCurrentUser()?.tenant ?? null;

/**
 * True only for an explicit master claim.
 *
 * Deliberately strict about the absent case: a session minted before this PR has
 * no `tenant` field, and defaulting that to master would show a legacy
 * INITIAL_ADMIN the master-only nav while their token still carries a tenant
 * claim — every request would then contradict the UI.
 */
export const isMasterContext = () => getTenant() === 'master';

/**
 * Enter an agency (INITIAL_ADMIN only), or return to master with `null`.
 *
 * The backend re-mints the JWT with the new tenant claim; we swap the stored
 * token, merge the tenant dimension into the session, then **hard reload**. The
 * reload is not incidental: App.js resolves the '/' dashboard in a `useMemo` with
 * an empty dependency array, so a client-side navigation after a claim change
 * renders the previous context's dashboard.
 *
 * On failure nothing is written — a 404/409 from the picker must leave the admin
 * exactly where they were, still able to pick again.
 */
export const selectAgency = async (agencyId) => {
  const response = await api.post('/auth/select-agency', { agencyId });
  const { token, agencyId: selectedId, agencyName } = response.data;

  sessionStorage.setItem('token', token);
  sessionStorage.setItem('user', JSON.stringify({
    // The spread preserves sessionVersion and primaryGroup -- an INITIAL_ADMIN's stays
    // null, and selecting an agency is not a placement.
    ...getCurrentUser(),
    agencyId: selectedId ?? null,
    agencyName: agencyName ?? null,
    tenant: selectedId == null ? 'master' : String(selectedId),
  }));

  hardRedirect('/');
  return response.data;
};

/**
 * One page of the agency registry. Returns the **raw Spring Page** — the picker
 * and the CRM both need `totalPages`/`last` to drive "Load more", which is
 * pagination state a flattened array cannot carry. Blank filters are omitted
 * rather than sent as empty strings.
 */
export const getAgenciesPage = async ({ search, status, page = 0, size = 50 } = {}) => {
  const params = { page, size };
  if (search) params.search = search;
  if (status) params.status = status;
  const response = await api.get('/admin/agencies', { params });
  return response.data;
};

/**
 * Paged platform-admin directory (P8-09), backed by the @MasterAllowed
 * DirectoryUserController.
 *
 * Returns the RAW Spring Page, like getAgenciesPage: the screen needs `last` to
 * drive "Load more". This is the only user-listing call that works under a master
 * claim — /api/users answers 403 TENANT_NOT_SELECTED there.
 */
export const getDirectoryUsers = async ({ search, page = 0, size = 50 } = {}) => {
  const params = { page, size };
  if (search) params.search = search;
  const response = await api.get('/admin/directory-users', { params });
  return response.data;
};

/**
 * Invites a new Veriform platform administrator (INITIAL_ADMIN).
 *
 * Takes no password: the invitee sets their own by redeeming the emailed link, so nobody
 * else ever knows it. Returns `{ admin, inviteUrl, inviteExpiresAt, emailSent }`.
 *
 * `inviteUrl` is null when the invitation was emailed — whoever holds the link can set the
 * password. It is present only when email is switched off, where it is the one way the flow
 * can complete: show it and let the admin copy it, but never persist it or write it to the
 * console. It is a live single-use activation credential.
 */
export const invitePlatformAdmin = async ({ firstName, lastName, username, email }) => {
  const response = await api.post('/admin/directory-users', { firstName, lastName, username, email });
  return response.data;
};

/**
 * Re-issues an administrator's invitation, invalidating any still outstanding.
 *
 * Returns the same shape as `invitePlatformAdmin`. `inviteUrl` is null when the invitation
 * was emailed; with email switched off it carries the replacement link, which matters more
 * here than on the first invitation: this call DESTROYS the previous link, so a UI that did
 * not surface the new one would leave the operator worse off than before they clicked.
 *
 * 409 if the administrator has already activated, or if their account is disabled.
 */
export const resendPlatformAdminInvite = async (id) => {
  const response = await api.post(`/admin/directory-users/${id}/invite`);
  return response.data;
};

/**
 * Kills an administrator's outstanding invitation link without sending another.
 *
 * 204 and idempotent. The account is untouched — still listed, still without a password;
 * `resendPlatformAdminInvite` issues a new link when one is wanted.
 */
export const revokePlatformAdminInvite = async (id) => {
  const response = await api.post(`/admin/directory-users/${id}/invite/revoke`);
  return response.data;
};

/**
 * Looks up an invitation so the accept page can name who it is for. UNAUTHENTICATED.
 *
 * POST with the token in the body, never a query parameter: a GET would put a live
 * activation credential into the request line, which the public origin's nginx logs
 * verbatim. 404 = never a valid link, 410 = real but spent/expired.
 */
export const previewAdminInvite = async (token) => {
  const response = await api.post('/auth/admin-invite/preview', { token });
  return response.data;
};

/**
 * Redeems an invitation, setting the invitee's own password. UNAUTHENTICATED.
 *
 * Answers 204 and deliberately returns NO session — the new administrator signs in
 * normally afterwards, through the ordinary login path with its 2FA.
 */
export const acceptAdminInvite = async (token, password) => {
  const response = await api.post('/auth/admin-invite/accept', { token, password });
  return response.data;
};

/**
 * Asks for a one-time password to be emailed ("Forgot password?"). UNAUTHENTICATED.
 *
 * The server answers 202 with one message whether or not an account matched, and mails
 * the one-time password only to the address on the account. Resolves to `{ message }`.
 * The person then signs in with it through `login`, which answers `passwordChangeRequired`.
 */
export const requestPasswordReset = async (identifier) => {
  const response = await api.post('/auth/password-reset/request', { identifier });
  return response.data;
};

/**
 * The forced password change after signing in with a one-time password. UNAUTHENTICATED —
 * until it succeeds nobody holds a session.
 *
 * `changeToken` is the grant `login` (or `verify2fa`) handed back; it goes in the body,
 * never a header. The server sets the new password and answers with the session, which is
 * stored here: saving the password is what completes the sign-in. 400 = the password was
 * refused (try another); 404 / 410 = the one-time password is spent or expired, or the
 * sign-in timed out (sign in again).
 */
export const completePasswordReset = async (changeToken, password) => {
  const response = await api.post('/auth/password-reset/complete', { token: changeToken, password });
  persistSession(response.data);
  return response.data;
};

/** Retry provisioning for a FAILED agency; 202, no body. */
export const retryAgencyProvisioning = async (id) => {
  const response = await api.post(`/admin/agencies/${id}/provision`);
  return response.data;
};

// Master catalog publish fan-out (P4-08's two AdminCatalogController mappings —
// the ONLY endpoints on that controller). Each answers 202 { queuedAgencyCount },
// which counts QUEUE ROWS WRITTEN, not deliveries: CatalogFanoutJob drains the
// queue on a 30s fixedDelay, so the correct wording for the user is "queued".
// Both are INITIAL_ADMIN-only, so a tenant admin calling them gets a 403.

/**
 * Queue a master catalog base form for fan-out to every agency. 202 { queuedAgencyCount }.
 *
 * ⚠️ NOT the same thing as {@link publishBaseForm} (issue #274). This is
 * `/admin/catalog/base-forms/{id}/publish` — the DISTRIBUTION of an already-PUBLISHED
 * master form to every tenant schema; it changes no status and is INITIAL_ADMIN-only.
 * The tenant-facing DRAFT → PUBLISHED transition is `/base-forms/{id}/publish`. Two
 * server paths that read alike and do entirely different things, which is how the UI
 * ended up with one "Publish" button that could never publish anything — hence the
 * distinct client names.
 */
export const distributeBaseFormToAgencies = async (id) => {
  const response = await api.post(`/admin/catalog/base-forms/${id}/publish`);
  return response.data;
};

/** Queue a master rank structure for fan-out. 202 { queuedAgencyCount }. */
export const publishRankStructure = async (id) => {
  const response = await api.post(`/admin/catalog/rank-structures/${id}/publish`);
  return response.data;
};

export const createInitialAdmin = async (userData) => {
  const response = await api.post('/auth/initial-admin', userData);
  return response.data;
};

// Returns the user list array. The backend exposes a paginated
// Page<UserSummaryResponse>; this helper unwraps the `content` field so the
// existing call sites (UserManagement, PolicyDistribution, BaseFormCreate)
// keep their array contract.
//
// When the caller does NOT pass an explicit `page`, this auto-pages: it walks
// pages (size 100) until a page returns fewer than `size` rows and returns the
// concatenated array — so screens with no pagination UI never silently drop
// records past the first page for larger tenants. When `page` IS passed, it
// preserves single-page behavior and returns just that page's rows.
export const getUsers = async ({ page, size = 100, ...filters } = {}) => {
  if (page != null) {
    const response = await api.get('/users', { params: { page, size, ...filters } });
    return response.data?.content ?? [];
  }

  const all = [];
  for (let p = 0; ; p++) {
    const response = await api.get('/users', { params: { page: p, size, ...filters } });
    const rows = response.data?.content ?? [];
    all.push(...rows);
    if (rows.length < size) break;
  }
  return all;
};

export const getUserById = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

/**
 * Creates an agency user and emails them the link they choose their own password from.
 *
 * It takes NO password — the server answers 400 to a body that carries one — because
 * nobody sets a password for somebody else (owner ruling, 2026-10-09).
 *
 * Resolves to `{ user, invitation }`. `invitation` is `{ email, inviteUrl, inviteExpiresAt,
 * emailSent }`, and `inviteUrl` is null whenever the invitation was emailed: the link is
 * handed back only when email is switched off, since nothing else can deliver it then.
 */
export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

/**
 * Sends someone's invitation again; whatever link was outstanding stops working.
 *
 * Resolves to the same `invitation` shape `createUser` answers with. 409 if the person has
 * already set a password — this is not a password reset — or the account is switched off.
 */
export const resendUserInvitation = async (id) => {
  const response = await api.post(`/users/${id}/invitation`);
  return response.data;
};

/**
 * Kills someone's outstanding invitation link without sending another. 204, idempotent.
 * The account stays on the roster, placed and ranked, and still without a password.
 */
export const revokeUserInvitation = async (id) => {
  const response = await api.post(`/users/${id}/invitation/revoke`);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

/**
 * Offboard a person (ORG-27, #322; decision D11 — users are never deleted).
 *
 * Ends every dated row they hold, voids the obligations they never met, hands their open
 * approvals to the next person up the chain and disables both identity rows. Everything
 * else is retained, because a historical report has to resolve them afterwards.
 *
 * Returns the report of what was ended — counts plus the submissions that changed hands
 * and the groups the person led. The dialog renders it; a bare "done" would hide that a
 * squad now has no leader and somebody else's queue just grew.
 *
 * @param {string} id
 * @param {{effectiveDate?: string, reason?: string}} body effectiveDate is an ISO date and
 *        may not be in the future; both fields are optional.
 */
export const offboardUser = async (id, body = {}) => {
  const response = await api.post(`/users/${id}/offboard`, body);
  return response.data;
};

/**
 * Put an offboarded person back on the books (ORG-27, #322).
 *
 * Clears the stamps and re-enables login. It creates no assignment and no rank: a
 * returning employee is placed and ranked deliberately, by an administrator making those
 * decisions now.
 */
export const reinstateUser = async (id) => {
  await api.post(`/users/${id}/reinstate`);
};

export const getCurrentUserInfo = async () => {
  const response = await api.get('/users/me');
  return response.data;
};

// No agency parameter: the bound schema IS the agency, so the endpoint takes
// none — every caller lists the bound tenant's groups.
//
// Reads are open to every authenticated user in the tenant (decision D15): the
// Organization screen is whole-agency and read-only for a REGULAR_USER, and the
// department era's single-group scoping is retired. Writes are AGENCY_ADMIN.
//
// `params` accepts { parentId, name, geographic, includeRetired }. `parentId` takes
// a group id or the literal 'root' (top level); `name` is only legal alongside a
// parentId, because a bare group name stopped identifying a group the moment squad
// letters could repeat under four districts.
export const getOrgGroups = async (params = {}) => {
  const response = await api.get('/org-groups', { params });
  return response.data;
};

// The nested tree, wrapped in a synthesized agency root node (isAgencyRoot: true) —
// the agency is the implicit root and has no row of its own.
export const getOrgGroupTree = async (params = {}) => {
  const response = await api.get('/org-groups/tree', { params });
  return response.data;
};

// The Organization screen's ONE read (ORG-23, #318): the nested tree, its people inside their
// groups, and the explicit supervisor edges between them.
//
// `group` on every node is the same OrgGroupSummaryResponse `/org-groups` serves, so the screen
// has one source of structure and no second fetch for the fields its edit dialog needs.
// Params: rootId (omit for the synthesized agency root), includeRetired, asOf.
export const getOrgTree = async (params = {}) => {
  const response = await api.get('/org-chart', { params });
  return response.data;
};

export const getOrgGroup = async (id) => {
  const response = await api.get(`/org-groups/${id}`);
  return response.data;
};

export const getOrgGroupChildren = async (id, params = {}) => {
  const response = await api.get(`/org-groups/${id}/children`, { params });
  return response.data;
};

// getMyOrgGroups is GONE (ORG-13, #308; decision D15). It called
// GET /api/org-groups/my-groups, which returned a REGULAR_USER a one-element list —
// their own group — so every picker built on it auto-selected that group and offered no
// way to look at any other part of the agency. The tree is whole-agency and read-only for
// everyone now: use getOrgGroupTree(). Pinned retired by retiredExports.test.js.

export const createOrgGroup = async (group) => {
  const response = await api.post('/org-groups', group);
  return response.data;
};

export const updateOrgGroup = async (id, body) => {
  const response = await api.put(`/org-groups/${id}`, body);
  return response.data;
};

// Re-parent. `parentId: null` means "move to the top level".
export const moveOrgGroup = async (id, body) => {
  const response = await api.post(`/org-groups/${id}/move`, body);
  return response.data;
};

// "Add squads…" — several children in ONE all-or-nothing call. Guide §6: the number
// of squads belongs in agency configuration, and four is not a universal maximum.
export const createOrgGroupChildren = async (parentId, body) => {
  const response = await api.post(`/org-groups/${parentId}/children:batch`, body);
  return response.data;
};

// Lifecycle (ORG-14, #309). Groups are RETIRED, never deleted (decision D11): there is
// no deleteOrgGroup here, and DELETE /api/org-groups/{id} answers 405 USE_RETIRE for any
// API client that tries. A retire is undone by one click on reactivate, which is why the
// dialog is a plain confirm rather than DeleteAgencyDialog's type-the-name ritual.
//
// `body` is optional: { endDate, reason }, both nullable. An absent endDate retires as
// of today; a future one is refused (A-14-2), because the blocking probes -- people,
// sub-groups, targeted forms -- are evaluated now and would go stale before the date.
export const retireOrgGroup = async (id, body = {}) => {
  const response = await api.post(`/org-groups/${id}/retire`, body);
  return response.data;
};

export const reactivateOrgGroup = async (id) => {
  const response = await api.post(`/org-groups/${id}/reactivate`);
  return response.data;
};

// The structural journal, newest first. Open to every authenticated user in the tenant
// (D15) -- "who renamed what, when" is navigational information about a tree everyone
// can already browse.
export const getOrgGroupHistory = async (id) => {
  const response = await api.get(`/org-groups/${id}/history`);
  return response.data;
};

// Who could fill this form out, and when the answer changed (ORG-14, #309).
// AGENCY_ADMIN only, matching the audience WRITE gate rather than its read gate.
export const getBaseFormAudienceHistory = async (id) => {
  const response = await api.get(`/base-forms/${id}/audience/history`);
  return response.data;
};

// Group types (ORG-11, #306) -- the agency's own word for each kind of group.
//
// The READ is open to every authenticated user in the tenant (D15): a REGULAR_USER's
// organization screen cannot render "Patrol Division" without it. `includeInactive` is
// for the admin panel alone, which owns the Status column and the reactivate action;
// every other caller wants the words a NEW group may be given, which is the default.
export const getOrgGroupTypes = async ({ includeInactive = false } = {}) => {
  const response = await api.get('/org-group-types', { params: { includeInactive } });
  return response.data;
};

// Sends no baseKind: a locally created type carries none, and the request DTO has no
// such field at all. Built-in kinds belong to the migration.
export const createOrgGroupType = async (body) => {
  const response = await api.post('/org-group-types', body);
  return response.data;
};

// A FULL replace of the editable fields -- send label, pluralLabel, geographic,
// sortOrder AND active every time. Omitting a flag is a 400, deliberately, so a rename
// can never be read as a deactivation.
export const updateOrgGroupType = async (id, body) => {
  const response = await api.put(`/org-group-types/${id}`, body);
  return response.data;
};

// 204, so there is no body to return. Refused with a 409 for a built-in, or while any
// group still uses the type -- the message names the groups and the caller renders it.
export const deleteOrgGroupType = async (id) => {
  await api.delete(`/org-group-types/${id}`);
};

// Admin Dashboard APIs
export const getAdminDashboardStats = async () => {
  const response = await api.get('/admin/dashboard/stats');
  return response.data;
};

export const getAgencyById = async (id) => {
  const response = await api.get(`/admin/agencies/${id}`);
  return response.data;
};

export const createAgency = async (agencyData) => {
  const response = await api.post('/admin/agencies', agencyData);
  return response.data;
};

export const updateAgency = async (id, agencyData) => {
  const response = await api.put(`/admin/agencies/${id}`, agencyData);
  return response.data;
};

export const deleteAgency = async (id) => {
  const response = await api.delete(`/admin/agencies/${id}`);
  return response.data;
};

// Agency Admin APIs (for AGENCY_ADMIN role)
export const getMyAgency = async () => {
  const response = await api.get('/agency-admin/my-agency');
  return response.data;
};

export const updateMyAgency = async (agencyData) => {
  const response = await api.put('/agency-admin/my-agency', agencyData);
  return response.data;
};

// Agency Tags APIs
export const getAgencyTags = async () => {
  const response = await api.get('/admin/tags');
  return response.data;
};

export const createAgencyTag = async (tagData) => {
  const response = await api.post('/admin/tags', tagData);
  return response.data;
};

export const updateAgencyTag = async (id, tagData) => {
  const response = await api.put(`/admin/tags/${id}`, tagData);
  return response.data;
};

export const deleteAgencyTag = async (id) => {
  const response = await api.delete(`/admin/tags/${id}`);
  return response.data;
};

// Base Form APIs (for INITIAL_ADMIN role)
export const getAllBaseForms = async (page = 0, size = 25) => {
  const response = await api.get(`/base-forms?page=${page}&size=${size}`);
  return response.data;
};

/**
 * One page of the forms in one status, as the raw Spring `Page` — the operator view asks for
 * `('PUBLISHED', 0, 1)` and reads `totalElements`, since nothing serves a count on its own.
 */
export const getBaseFormsPageByStatus = async (status, page = 0, size = 25) => {
  const response = await api.get(`/base-forms/status/${status}`, { params: { page, size } });
  return response.data;
};

export const getBaseFormById = async (id) => {
  const response = await api.get(`/base-forms/${id}`);
  return response.data;
};

export const createBaseForm = async (formData) => {
  const response = await api.post('/base-forms', formData);
  return response.data;
};

export const updateBaseForm = async (id, formData) => {
  const response = await api.put(`/base-forms/${id}`, formData);
  return response.data;
};

export const deleteBaseForm = async (id) => {
  const response = await api.delete(`/base-forms/${id}`);
  return response.data;
};

/**
 * Copy a form into a new tenant-local DRAFT (201). The sanctioned way to get an
 * editable version of a read-only "Catalog" row — the 409 on editing one says so.
 */
export const cloneBaseForm = async (id) => {
  const response = await api.post(`/base-forms/${id}/clone`);
  return response.data;
};

/**
 * Retire a form (200). A POST transition rather than a PUT because a PUBLISHED
 * master catalog form cannot be updated at all (WD-9), so archiving cannot ride
 * on updateBaseForm — it is the one permitted way out of PUBLISHED.
 */
export const archiveBaseForm = async (id) => {
  const response = await api.post(`/base-forms/${id}/archive`);
  return response.data;
};

/**
 * DRAFT → PUBLISHED (200, issue #274) — the transition that makes an authored form
 * reachable by the officers meant to fill it out. Idempotent on an already-published
 * form; 409 on an ARCHIVED one (archive is terminal — clone instead).
 *
 * ⚠️ See {@link distributeBaseFormToAgencies} for the master fan-out, which is a
 * different endpoint doing a different thing.
 */
export const publishBaseForm = async (id) => {
  const response = await api.post(`/base-forms/${id}/publish`);
  return response.data;
};

/**
 * PUBLISHED → DRAFT (200, issue #274) — the reversible removal. On a master-catalog
 * copy this is the agency's only lever over content it did not author and cannot
 * edit, so it stays available there; `updateBaseForm` and `archiveBaseForm` both 409
 * on such a row.
 */
export const unpublishBaseForm = async (id) => {
  const response = await api.post(`/base-forms/${id}/unpublish`);
  return response.data;
};

/**
 * The form's group audience — `{ orgGroupIds: [...] }`. An empty list means
 * agency-wide (every user sees it), NOT "nobody". 404 for a form the caller may not
 * see, mirroring the by-id read.
 */
export const getBaseFormAudience = async (id) => {
  const response = await api.get(`/base-forms/${id}/audience`);
  return response.data;
};

/**
 * Replace the form's group audience (200). Pass an empty array to clear it,
 * which restores agency-wide visibility.
 *
 * `targets` is `[{ orgGroupId, includeDescendants }]` since ORG-21 (#316) — a group target
 * reaches the groups beneath it unless the admin explicitly opts out. Omitting
 * `includeDescendants` means `true` on the server, which is the safe, guide-shaped answer.
 */
export const setBaseFormAudience = async (id, targets) => {
  const response = await api.put(`/base-forms/${id}/audience`, { targets });
  return response.data;
};

// 2FA APIs
export const verify2fa = async (tempToken, code) => {
  const response = await api.post('/auth/verify-2fa', { token: tempToken, code });

  // A sign-in that began with an emailed one-time password ends in a forced password
  // change, not a session, even after the authenticator code.
  const unfinished = unfinishedSignIn(response.data);
  if (unfinished) return unfinished;

  persistSession(response.data);
  return response.data;
};

export const get2faStatus = async () => {
  const response = await api.get('/2fa/status');
  return response.data;
};

export const setup2fa = async () => {
  const response = await api.get('/2fa/setup');
  return response.data;
};

export const enable2fa = async (secret, code) => {
  const response = await api.post('/2fa/enable', { secret, code });
  return response.data;
};

export const disable2fa = async (code) => {
  const response = await api.post('/2fa/disable', { code });
  return response.data;
};

// Rank Structure APIs
//
// `getRankStructures` returns a SUMMARY list (no `ranks` array — has
// `rankCount` instead). The list endpoint is projection-backed for the JPA
// remediation; lazy-loading every structure's ranks collection per row was a
// classic N+1 in the legacy entity-based version. Use `getRankStructure(id)`
// for the detail view (full ranks) or `getRankStructuresWithRanks()`
// when a caller actually needs every structure's rank array (single SQL via
// the `RankStructure.withRanks` named entity graph).
export const getRankStructures = async () => {
  const response = await api.get('/rank-structures');
  return response.data;
};

export const getRankStructuresWithRanks = async () => {
  const response = await api.get('/rank-structures/with-ranks');
  return response.data;
};

export const getRankStructure = async (id) => {
  const response = await api.get(`/rank-structures/${id}`);
  return response.data;
};

// THE agency's rank ladder, with its ranks (ORG-06, #301). Every rank picker in the app
// renders this ONE structure: the Users form, the document upload gate and the directory
// filter. They used to flatten every structure in the schema into one list, which let an
// operator gate a document on a ladder nobody in the agency holds — the clearance rule's
// structure-mismatch branch then denied it to every reader, including the intended
// audience.
//
// 404 when the schema has none (master holds catalog originals, not an agency's choice),
// so callers resolve to null and render "no ranks configured" rather than an error.
export const getActiveRankStructure = async () => {
  try {
    const response = await api.get('/rank-structures/active');
    return response.data;
  } catch (err) {
    if (err.response?.status === 404) {
      return null;
    }
    throw err;
  }
};

export const createRankStructure = async (data) => {
  const response = await api.post('/rank-structures', data);
  return response.data;
};

export const updateRankStructure = async (id, data) => {
  const response = await api.put(`/rank-structures/${id}`, data);
  return response.data;
};

export const deleteRankStructure = async (id) => {
  const response = await api.delete(`/rank-structures/${id}`);
  return response.data;
};

export const cloneRankStructure = async (id, data) => {
  const response = await api.post(`/rank-structures/${id}/clone`, data);
  return response.data;
};

export const addRank = async (structureId, data) => {
  const response = await api.post(`/rank-structures/${structureId}/ranks`, data);
  return response.data;
};

export const updateRank = async (structureId, rankId, data) => {
  const response = await api.put(`/rank-structures/${structureId}/ranks/${rankId}`, data);
  return response.data;
};

export const deleteRank = async (structureId, rankId) => {
  const response = await api.delete(`/rank-structures/${structureId}/ranks/${rankId}`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Choosing the agency's ladder (ORG-07, #302)
//
// An agency has at most ONE active rank structure, and switching to another is only
// safe with a complete mapping: every rank someone currently holds, and every rank a
// document is gated on, has to be told what it becomes. The preview is what makes that
// answerable in a dialog — without it the only way to learn which ranks are stranded
// would be to parse prose out of a 409 body.

// What activating this structure would require: the stranded ranks with their holder and
// document counts, plus the target's assignable (non-retired) ranks for the <select>.
export const getRankActivationPreview = async (id) => {
  const response = await api.get(`/rank-structures/${id}/activation-preview`);
  return response.data;
};

// Makes this structure THE agency's ladder. `data` is { rankMappings: [{fromRankId,
// toRankId}, ...] } — [] when the preview reported nothing stranded. A 409 means the
// tenant's data changed since the preview (someone was promoted meanwhile): re-fetch the
// preview rather than retrying the same body.
export const activateRankStructure = async (id, data) => {
  const response = await api.post(`/rank-structures/${id}/activate`, data);
  return response.data;
};

// Closes a rank to NEW holders. Everyone already holding it keeps it and keeps clearing
// documents with it; every document gated on it stays gated (D11 — end-date everything,
// delete nothing). Idempotent.
export const retireRank = async (structureId, rankId) => {
  const response = await api.post(`/rank-structures/${structureId}/ranks/${rankId}/retire`);
  return response.data;
};

export const unretireRank = async (structureId, rankId) => {
  const response = await api.post(`/rank-structures/${structureId}/ranks/${rankId}/unretire`);
  return response.data;
};

// Marks one MASTER original as the catalog default — the ladder every newly provisioned
// tenant is activated onto. Not a fan-out: it queues nothing and changes no tenant, which
// is why it answers 200 with the structure rather than 202 with a count. Refused with a
// 409 under a tenant claim.
export const markRankStructureCatalogDefault = async (id) => {
  const response = await api.post(`/admin/catalog/rank-structures/${id}/make-default`);
  return response.data;
};

// ---------------------------------------------------------------------------
// A person's rank record (ORG-06, #301)
// ---------------------------------------------------------------------------
//
// Rank is a fact about the PERSON, not about their org placement, so it has its own
// endpoints rather than riding on PUT /users/{id} — a promotion is a dated fact with an
// effective date, a note and an actor, which a full-entity update cannot express. That
// is how a routine profile edit used to silently re-rank someone (issue #158).

/**
 * The person's rank record: what they hold, plus every row ever written.
 *
 * `asOf` (ORG-28, #323) re-dates the CURRENT half — "what did they hold last March" — and
 * leaves `history` alone, because it has always been complete. There is deliberately no
 * `includeEnded`: the server has nothing to switch off with it.
 */
export const getUserRanks = async (userId, { asOf } = {}) => {
  const response = await api.get(`/users/${userId}/ranks`, { params: { asOf } });
  return response.data;
};

export const assignUserRank = async (userId, data) => {
  const response = await api.post(`/users/${userId}/ranks`, data);
  return response.data;
};

export const endUserRank = async (userRankId, data) => {
  const response = await api.post(`/user-ranks/${userRankId}/end`, data);
  return response.data;
};

export const correctUserRank = async (userRankId, data) => {
  const response = await api.patch(`/user-ranks/${userRankId}`, data);
  return response.data;
};

// Membership APIs (ORG-16, #311)
//
// These replace the four exports that used to sit here. Two things were wrong with them
// and both die here: they were unused by any component, and — measured on this branch —
// three of the four pointed at paths the backend has never served. They were renamed
// from `…HierarchyAssignment…` to `…OrgAssignment…` with the entity in ORG-08 (#303),
// but the SERVER paths stayed `/api/…/hierarchy-assignments`, so a caller of
// `createOrgAssignment` would have got a 404. Nothing called them, so nothing broke; the
// dated write API below is what the roster and the assignments panel actually use.

/**
 * A group's roster. `mode` is PRIMARY (whose home group is this) or ANY (who works here).
 *
 * `asOf` (ORG-28, #323) answers "who was in this group on that day". It moves the temporal
 * WINDOW, not the tree: `includeDescendants` still expands through today's structure
 * (A-11.10), which is why the screen labels a non-today reading rather than leaving the
 * caller to assume otherwise.
 */
export const getOrgGroupMembers = async (groupId, { includeDescendants, mode, asOf } = {}) => {
  const response = await api.get(`/org-groups/${groupId}/members`, {
    params: { includeDescendants, mode, asOf },
  });
  return response.data;
};

export const addOrgGroupMember = async (groupId, data) => {
  const response = await api.post(`/org-groups/${groupId}/members`, data);
  return response.data;
};

/** Corrects a row's position title or its own dates. A MOVE is a new row, not a PATCH. */
export const updateOrgAssignment = async (assignmentId, data) => {
  const response = await api.patch(`/org-assignments/${assignmentId}`, data);
  return response.data;
};

/**
 * Ends a membership. POST with a body rather than DELETE: it records an end DATE and a
 * reason, and nothing is removed (decision D11).
 */
export const endOrgAssignment = async (assignmentId, data) => {
  const response = await api.post(`/org-assignments/${assignmentId}/end`, data ?? {});
  return response.data;
};

export const makeOrgAssignmentPrimary = async (assignmentId, data) => {
  const response = await api.post(`/org-assignments/${assignmentId}/make-primary`, data ?? {});
  return response.data;
};

export const voidOrgAssignment = async (assignmentId, data) => {
  const response = await api.post(`/org-assignments/${assignmentId}/void`, data);
  return response.data;
};

/**
 * One person's memberships. Re-pointed at the path the server actually serves.
 *
 * ⚠️ `asOf` and `includeEnded` are mutually exclusive and the server answers 400 for both
 * (A-28-1): one means "the rows live on one date" and the other "every row ever", and together
 * one would silently win.
 */
export const getUserOrgAssignments = async (userId, { includeEnded, asOf } = {}) => {
  const response = await api.get(`/users/${userId}/assignments`, {
    params: { includeEnded, asOf },
  });
  return response.data;
};

/**
 * Everything this person's organizational record has ever said, as ONE list (ORG-28, #323) —
 * assignments, details, leadership changes, ranks, supervisor edges and a past offboarding,
 * newest first, with ended and voided rows included and flagged.
 *
 * Readable by the person themselves, an agency administrator, or a GROUP_ADMIN whose subtree
 * currently contains them (A-11.9). Anyone else gets a 403 with a body worth rendering, so the
 * caller shows `serverMessage(err, …)` inline rather than swallowing it.
 */
export const getUserOrgHistory = async (userId) => {
  const response = await api.get(`/users/${userId}/org-history`);
  return response.data;
};

/**
 * Marks a rank row as never having been true (AS-14) — the third of the three voids, beside
 * `voidOrgAssignment` and `voidSupervisorRelationship`. `{ reason }` is required; all three
 * share one server-side rule, so the 403 reads the same on each.
 */
export const voidUserRank = async (userRankId, data) => {
  const response = await api.post(`/user-ranks/${userRankId}/void`, data);
  return response.data;
};

/**
 * Designate a group's leader (ORG-17, #312) — the fact the whole derived chain of command
 * hangs off. `{ userId, effectiveDate, positionTitle }`; answers the new roster line.
 *
 * The date is REQUIRED rather than defaulted: taking command is a dated fact somebody will
 * later read out of the record, and a silent default would stamp the day the form was
 * submitted rather than the day the order took effect.
 */
export const designateGroupLeader = async (groupId, data) => {
  const response = await api.post(`/org-groups/${groupId}/leader`, data);
  return response.data;
};

/**
 * Stand a group's leadership down. 204, and idempotent — a group with no leader is a
 * success, so a retry never 404s.
 *
 * ⚠️ A DELETE here does NOT contradict ORG-14 (#309)'s `USE_RETIRE` on a group or ORG-18
 * (#313)'s `USE_END` on a relationship: nothing is deleted. It ends a dated ATTRIBUTE of a
 * membership row that survives, and the outgoing leader stays a member of the group.
 */
export const vacateGroupLeader = async (groupId, data) => {
  await api.delete(`/org-groups/${groupId}/leader`, { data: data ?? {} });
};

/**
 * Active groups with nobody in command, inside the caller's admin scope (ORG-17, #312) —
 * the recovery surface for "the chain of command stops here".
 */
export const getVacantCommand = async () => {
  const response = await api.get('/org-groups/vacant-command');
  return response.data;
};

// Supervisor Relationship APIs
//
// ⚠️ Two different response shapes, and the split is deliberate.
//
// /supervisors and /supervisor-history answer with SupervisorRelationshipResponse
// ({ id, supervisorId, supervisorName, relationshipType, via, derived, effectiveDate,
// endDate, reason, endReason, voidedAt, voidReason, active }). ORG-17 (#312) had moved
// /supervisors to the chain shape; ORG-18 (#313) moved it back, because an edge can be
// ENDED and VOIDED on a date with a reason and none of that fits on a chain link. A DERIVED
// link comes back with a null `id` and `derived: true` — the honest signal that there is
// nothing to end, and what the UI hides the End control on.
//
// /subordinates and /chain-of-command still answer with ChainLinkResponse
// ({ userId, username, rankName, groupId, groupName, via }): they describe PEOPLE, and
// nothing ends a subordinate.
//
// Render every label through services/relationshipLabels.js, never from the raw enum.
export const getUserSupervisors = async (userId, { asOf, includeEnded } = {}) => {
  const params = {};
  if (asOf) params.asOf = asOf;
  if (includeEnded) params.includeEnded = true;
  const response = await api.get(`/users/${userId}/supervisors`, { params });
  return response.data;
};

export const getUserSubordinates = async (userId) => {
  const response = await api.get(`/users/${userId}/subordinates`);
  return response.data;
};

export const getUserChainOfCommand = async (userId) => {
  const response = await api.get(`/users/${userId}/chain-of-command`);
  return response.data;
};

export const createSupervisorRelationship = async (subordinateId, data) => {
  const response = await api.post(`/supervisor-relationships?subordinateId=${subordinateId}`, data);
  return response.data;
};

export const updateSupervisorRelationship = async (id, data) => {
  const response = await api.put(`/supervisor-relationships/${id}`, data);
  return response.data;
};

// ORG-18 (#313): an edge is ENDED, never deleted -- DELETE answers 405 USE_END. The
// deleteSupervisorRelationship export that used to sit here is retired and is named in
// retiredExports.test.js so it cannot come back.
export const endSupervisorRelationship = async (id, data = {}) => {
  const response = await api.post(`/supervisor-relationships/${id}/end`, data);
  return response.data;
};

export const voidSupervisorRelationship = async (id, data) => {
  const response = await api.post(`/supervisor-relationships/${id}/void`, data);
  return response.data;
};

export const getUserSupervisorHistory = async (userId) => {
  const response = await api.get(`/users/${userId}/supervisor-history`);
  return response.data;
};

// Notification APIs
export const getNotifications = async ({ page = 0, size = 20 } = {}) => {
  const response = await api.get('/notifications', { params: { page, size } });
  return response.data;
};

export const getUnreadNotifications = async ({ page = 0, size = 20 } = {}) => {
  const response = await api.get('/notifications/unread', { params: { page, size } });
  return response.data;
};

export const getUnreadNotificationCount = async () => {
  const response = await api.get('/notifications/unread-count');
  return response.data;
};

export const markNotificationAsRead = async (id) => {
  const response = await api.put(`/notifications/${id}/read`);
  return response.data;
};

export const markAllNotificationsAsRead = async () => {
  const response = await api.put('/notifications/read-all');
  return response.data;
};

export const deleteNotification = async (id) => {
  const response = await api.delete(`/notifications/${id}`);
  return response.data;
};

// Form Submission APIs
export const createFormSubmission = async (data) => {
  const response = await api.post('/form-submissions', data);
  return response.data;
};

export const updateFormSubmission = async (id, data) => {
  const response = await api.put(`/form-submissions/${id}`, data);
  return response.data;
};

export const submitForm = async (id, data) => {
  const response = await api.post(`/form-submissions/${id}/submit`, data);
  return response.data;
};

export const getFormSubmissions = async (params = {}) => {
  const queryString = Object.entries(params)
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  const response = await api.get(`/form-submissions${queryString ? '?' + queryString : ''}`);
  return response.data;
};

export const getFormSubmission = async (id) => {
  const response = await api.get(`/form-submissions/${id}`);
  return response.data;
};

export const getFormSubmissionAuditLog = async (id) => {
  const response = await api.get(`/form-submissions/${id}/audit-log`);
  return response.data;
};

export const approveFormSubmission = async (id, data) => {
  const response = await api.post(`/form-submissions/${id}/approve`, data);
  return response.data;
};

export const rejectFormSubmission = async (id, data) => {
  const response = await api.post(`/form-submissions/${id}/reject`, data);
  return response.data;
};

export const delegateFormSubmission = async (id, data) => {
  const response = await api.post(`/form-submissions/${id}/delegate`, data);
  return response.data;
};

export const cancelFormSubmission = async (id, data) => {
  const response = await api.post(`/form-submissions/${id}/cancel`, data);
  return response.data;
};

export const getPendingApprovals = async () => {
  const response = await api.get('/form-submissions/pending-approvals');
  return response.data;
};

// Drafts the caller's people have started and not submitted (product-owner request
// "Supervisor view of draft forms"). The server decides whose: direct and indirect
// reports, a group administrator's groups, or the whole agency for an agency admin.
export const getTeamDrafts = async () => {
  const response = await api.get('/form-submissions/team-drafts');
  return response.data;
};

export const getMySubmissions = async (params = {}) => {
  const queryString = Object.entries(params)
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  const response = await api.get(`/form-submissions/my-submissions${queryString ? '?' + queryString : ''}`);
  return response.data;
};

// Form Submission Attachment APIs (Phase 4)
// Attachments follow the same allowed-type/size policy as Documents uploads;
// upload/delete are submitter-only while the submission is DRAFT/RETURNED.
export const uploadSubmissionAttachment = async (submissionId, file, fieldId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fieldId', String(fieldId));
  const response = await api.post(`/form-submissions/${submissionId}/attachments`, formData, {
    // Let the browser set the multipart boundary (same as uploadDocument).
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

export const getSubmissionAttachments = async (submissionId) => {
  const response = await api.get(`/form-submissions/${submissionId}/attachments`);
  return response.data;
};

export const deleteSubmissionAttachment = async (submissionId, attachmentId) => {
  const response = await api.delete(`/form-submissions/${submissionId}/attachments/${attachmentId}`);
  return response.data;
};

export const getSubmissionAttachmentDownloadUrl = async (submissionId, attachmentId) => {
  const response = await api.get(`/form-submissions/${submissionId}/attachments/${attachmentId}/download`);
  return response.data.url;
};

/**
 * An attachment's raw bytes, for the in-app viewer.
 *
 * BOTH ids stay in the path, always: the server authorizes the SUBMISSION first
 * (assertViewerCanAccess) and only then loads the attachment belonging to it. An
 * attachment-id-only route would be a cross-submission read handle.
 *
 * `responseType: 'blob'` for the same reason as the document endpoint — axios
 * would otherwise string-decode the bytes into a corrupt blob.
 */
export const fetchSubmissionAttachmentContentBlob = async (submissionId, attachmentId, { signal } = {}) => {
  const response = await api.get(
    `/form-submissions/${submissionId}/attachments/${attachmentId}/content`,
    { responseType: 'blob', signal }
  );
  return response.data;
};

/** The plain-text rendition of a non-PDF attachment. Both ids, same reason as above. */
export const getSubmissionAttachmentTextPreview = async (submissionId, attachmentId, { signal } = {}) => {
  const response = await api.get(
    `/form-submissions/${submissionId}/attachments/${attachmentId}/preview-text`,
    { signal }
  );
  return response.data;
};

// Policy Acknowledgement APIs
export const distributePolicyDocument = async (data) => {
  const response = await api.post('/policy-acknowledgements/distribute', data);
  return response.data;
};

/**
 * What this exact distribution would do, computed by the SERVER (ORG-21, #316).
 *
 * Same body as `distributePolicyDocument` and the same refusals, because both run one
 * `resolveRecipients` on the server — so the number an admin approves is the number of rows
 * that get written. The screen used to count client-side from whichever users it had loaded,
 * which could not agree with the server about anybody it had not.
 */
export const previewPolicyDistribution = async (data) => {
  const response = await api.post('/policy-acknowledgements/preview', data);
  return response.data;
};

export const getPendingAcknowledgements = async ({ page = 0, size = 20 } = {}) => {
  const response = await api.get('/policy-acknowledgements/pending', {
    params: { page, size }
  });
  return response.data;
};

export const getAllAcknowledgements = async ({ page = 0, size = 20 } = {}) => {
  const response = await api.get('/policy-acknowledgements/all', {
    params: { page, size }
  });
  return response.data;
};

// Sign several policies with one typed signature (product-owner request "Sign All
// Option for Policies"). Sends exactly the ids the officer confirmed; answers
// { signed: [...], skipped: [{ id, reason }] }.
export const signAllPolicies = async (acknowledgementIds, eSignature) => {
  const response = await api.post('/policy-acknowledgements/sign-all', { acknowledgementIds, eSignature });
  return response.data;
};

/**
 * What the signing dialog needs for one owed policy (DOC-19, #467): the obligation's revision and its
 * current file (a signer cannot list revisions), the reading time and the seconds left of it, and the
 * refusal a signature would meet now — the same verdict the signature runs.
 */
export const getSigningContext = async (id) => {
  const response = await api.get(`/policy-acknowledgements/${id}/signing`);
  return response.data;
};

export const acknowledgePolicy = async (id, data) => {
  const response = await api.post(`/policy-acknowledgements/${id}/acknowledge`, data);
  return response.data;
};

// The test a revision asks its signers to pass (DOC-26 #474, taken in DOC-28 #476): the questions with
// their options' ids and labels, `passPercent`, `passed` and `attemptCount` — never the answer key. The
// row's own person only, refused as the signature is for a reason no test fixes; 404 when the revision
// asks for no test.
export const getAcknowledgementTest = async (id) => {
  const response = await api.get(`/policy-acknowledgements/${id}/test`);
  return response.data;
};

// {answers: [{questionId, optionIds}]}, every question once → 201 with `passed`, `scorePercent`,
// `correctCount`, `questionCount`, `passPercent` and `incorrectQuestionIds`. 409 TEST_ALREADY_PASSED once
// passed (they can sign), TEST_CHANGED when the test is no longer the one served (reload it).
export const submitAcknowledgementTestAttempt = async (id, payload) => {
  const response = await api.post(`/policy-acknowledgements/${id}/test/attempts`, payload);
  return response.data;
};

// DOC-29 (#477): the recall cards an owed policy asks its signer to page through first, in order, each with
// its answer (ungraded), plus `completed` / `completedAt`. 404 when the revision has none; the signature's
// own refusals otherwise (REVISION_SUPERSEDED, DOCUMENT_NOT_READABLE, a row no longer owed).
export const getAcknowledgementRecallCards = async (id) => {
  const response = await api.get(`/policy-acknowledgements/${id}/recall-cards`);
  return response.data;
};

// The signer has paged through every card: recorded once per revision, so a repeat answers with the first
// time. → {completedAt}.
export const completeAcknowledgementRecallCards = async (id) => {
  const response = await api.put(`/policy-acknowledgements/${id}/recall-cards/completion`);
  return response.data;
};

// Per-document acknowledgement summaries for distributions the current admin
// sent themselves (Phase 5 "My Policy Distributions" widget). Backend scopes
// by sentBy = current user; GROUP_ADMIN and above only.
export const getMyDistributions = async () => {
  const response = await api.get('/policy-acknowledgements/my-distributions');
  return response.data;
};

/**
 * The compliance report for one document.
 *
 * ORG-21 (#316): the group filter reaches its sub-groups unless told otherwise, and the
 * options object replaced the positional second argument so the flag has somewhere to live.
 * With no `orgGroupId` the server narrows to the caller's own admin scope — the whole agency
 * for an AGENCY_ADMIN, their own subtree for a GROUP_ADMIN.
 */
export const getAcknowledgementReport = async (
  documentId, { orgGroupId, includeDescendants, groupBy, rollUp, revisionId, allRevisions } = {}
) => {
  const params = { documentId };
  if (orgGroupId) {
    params.orgGroupId = orgGroupId;
    // Only meaningful alongside a group, and sent explicitly rather than relying on the
    // server default so the request says what the checkbox says.
    params.includeDescendants = includeDescendants !== false;
  }
  // ORG-22 (#317): SNAPSHOT (the position at distribution) or CURRENT (today). Sent only
  // when the caller names one, so the server's own default — SNAPSHOT, assumption AS-23 —
  // stays the single definition of "what you get if you do not ask".
  if (groupBy) {
    params.groupBy = groupBy;
  }
  // ORG-25 (#320): what `byGroup[]` is keyed on — DIRECT (one row per group) or GEOGRAPHIC
  // (one row per district). Orthogonal to `groupBy`, which chooses WHICH POSITION a row is
  // reported at; a geographic roll-up of snapshot positions is a sensible request.
  if (rollUp) {
    params.rollUp = rollUp;
  }
  // DOC-30 (#478): which revision the report is about. Neither sent means the server's default, the
  // published revision (owner ruling 2026-10-02); `allRevisions` is every revision, one row per person
  // per revision, as the report read before DOC-30.
  if (allRevisions) {
    params.allRevisions = true;
  } else if (revisionId) {
    params.revisionId = revisionId;
  }
  const response = await api.get('/policy-acknowledgements/report', { params });
  return response.data;
};

/**
 * Who the holder of a submission's active approval step may delegate it to (ORG-22, #317).
 *
 * Step-scoped rather than a user list (assumption A-22-1): `GET /api/users` is
 * GROUP_ADMIN-only, and the approver delegating a step is very often a REGULAR_USER. The
 * authorization is that you hold the step, which is the same rule that makes the delegation
 * itself legal.
 *
 * Returns the raw Spring `Page` — the picker renders `content` and reads `totalElements` to
 * say whether there is more to find than it is showing.
 */
export const getDelegateCandidates = async (submissionId, { search, page = 0, size = 20 } = {}) => {
  const params = { page, size };
  if (search) {
    params.search = search;
  }
  const response = await api.get(`/form-submissions/${submissionId}/delegate-candidates`, { params });
  return response.data;
};

// Dashboard Stats APIs
export const getUserDashboardStats = async () => {
  const response = await api.get('/dashboard/my-stats');
  return response.data;
};

export const getDashboardPendingApprovals = async () => {
  const response = await api.get('/dashboard/pending-approvals');
  return response.data;
};

// ORG-25 (#320). All three take an OPTIONAL options object and omit absent keys rather than
// sending them as `undefined` — axios serialises an undefined param as an empty value, and
// `?orgGroupId=` is a client that failed to populate a field, which the server refuses on
// purpose. Defaulting the argument keeps every existing zero-argument call site working.
const definedParams = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null));

export const getAdminFormStats = async ({ orgGroupId, includeDescendants } = {}) => {
  const response = await api.get('/dashboard/form-stats', {
    params: definedParams({ orgGroupId, includeDescendants }),
  });
  return response.data;
};

export const getAdminAcknowledgementStats = async ({ orgGroupId, includeDescendants } = {}) => {
  const response = await api.get('/dashboard/acknowledgement-stats', {
    params: definedParams({ orgGroupId, includeDescendants }),
  });
  return response.data;
};

/**
 * Per-group roll-ups with subtree totals, scoped to the caller's admin subtree.
 *
 * Every number in the response is a subtree total the SERVER computed. Nothing that renders it
 * may re-sum children in JavaScript: two sums that disagree is precisely the failure this
 * endpoint exists to prevent.
 */
export const getGroupStats = async ({ rootId, depth, by, asOf } = {}) => {
  const response = await api.get('/dashboard/group-stats', {
    params: definedParams({ rootId, depth, by, asOf }),
  });
  return response.data;
};

// Org Chart & Directory APIs
//
// ORG-23 (#318) removed getOrgGroupOrgChart. It served a supervisor graph of PEOPLE rooted in
// one group, built with one query per person (#77); getOrgTree below replaces it with the whole
// agency's tree and its personnel in one call. retiredExports.test.js keeps the old name from
// coming back.

// ORG-24 (#319) removed getOrgGroupDirectory. It called GET /api/org-groups/{id}/directory,
// which served ONE group node's flat roster: a division's directory showed nobody from its
// squads, and somebody the org model had not placed appeared under no group anywhere (#229).
// The endpoint is gone, so re-introducing the export would 404 rather than answer narrowly;
// retiredExports.test.js keeps the name from coming back.

// Blank, null and undefined parameters are OMITTED rather than sent empty. axios would
// otherwise serialise `search=''`, which the server normalises back to null anyway — but
// `includeDescendants=''` would be a binding failure, and an omitted parameter is the only
// form that lets the server's own default apply.
const withoutBlanks = (params) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value != null && value !== ''),
);

// THE employee directory (ORG-24, #319): the whole agency, one page at a time.
//
// Returns the RAW Spring `Page` rather than its content, like getAgenciesPage and for the same
// reason — the caller renders "Showing X–Y of Z" and disables Next at the end, which needs
// `totalElements`, `number` and `last`. Flattening to the array here would make the pager
// guess.
//
// `params` accepts { orgGroupId, includeDescendants, membership, rankId, supervisorId, search,
// status, location, geographicCommandId, asOf, page, size }. `asOf` (ORG-28, #323) reads the
// placements and ranks as they stood on that day, against today's tree (A-11.10). Blank and undefined values are omitted
// so the server applies its own defaults — and BOTH inclusive defaults are decision D7, so
// omitting `includeDescendants` or `membership` is the safe direction: a filter on a division
// reaches its squads, and somebody detailed into a group counts as a member of it.
export const getDirectory = async (params = {}) => {
  const response = await api.get('/directory', { params: withoutBlanks(params) });
  return response.data;
};

/**
 * Every row matching the CURRENT FILTER, across all pages — not the visible page (AS-28).
 *
 * A page-only export silently misleads whoever files it: a compliance officer exporting
 * "everyone in Patrol Division" and getting the first 25 of 140 has no way to tell. So this
 * walks pages at the server's cap until `last`, and returns `{ rows, truncated }` rather than a
 * bare array — the caller has to be able to SAY that it stopped early, which is the other half
 * of not misleading anyone.
 *
 * The 50-page ceiling is a real limit, not a formality: at 100 rows a page that is 5 000
 * people, and an agency larger than that should narrow the filter rather than discover the
 * truncation in a spreadsheet.
 */
export const MAX_DIRECTORY_EXPORT_PAGES = 50;
export const DIRECTORY_EXPORT_PAGE_SIZE = 100;

export const exportDirectoryCsv = async (params = {}) => {
  const rows = [];
  let page = 0;
  let truncated = false;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- the pages are sequential by nature: the
    // request for page N+1 is only worth making once page N says it is not the last.
    const data = await getDirectory({ ...params, page, size: DIRECTORY_EXPORT_PAGE_SIZE });
    rows.push(...(data?.content || []));
    page += 1;
    if (data?.last !== false) {
      break;
    }
    if (page >= MAX_DIRECTORY_EXPORT_PAGES) {
      truncated = true;
      break;
    }
  }
  return { rows, truncated };
};

// Notification Preferences APIs
export const getNotificationPreferences = async () => {
  const response = await api.get('/notification-preferences');
  return response.data;
};

export const updateNotificationPreferences = async (preferences) => {
  const response = await api.put('/notification-preferences', preferences);
  return response.data;
};

export const updateNotificationPreference = async (type, data) => {
  const response = await api.put(`/notification-preferences/${type}`, data);
  return response.data;
};

// Temporary assignments — "details" (ORG-19, #314)
//
// A detail sends someone to work for another group, or to another location while they stay in
// the same group, for a bounded period. It never changes the permanent organization chart, so
// none of these calls touches /org-groups/{id}/members.
//
// ⚠️ There is no deleteDetail, and there will not be. Nothing is deleted (decision D11):
// `endDetail` rewrites the end date and records who ended it and why, and the row survives so a
// report can still resolve it years later.

/** Record a detail for one person. 201 with the created row. */
export const createDetail = async (userId, data) => {
  const response = await api.post(`/users/${userId}/details`, data);
  return response.data;
};

export const getDetail = async (id) => {
  const response = await api.get(`/details/${id}`);
  return response.data;
};

/**
 * Push a running detail's end date out. The end date is the ONLY thing this changes — moving
 * the group, the start date or the location means ending this detail and recording a new one.
 */
export const extendDetail = async (id, data) => {
  const response = await api.post(`/details/${id}/extend`, data);
  return response.data;
};

/** Cut a detail short. POST, not DELETE — see the note above. */
export const endDetail = async (id, data) => {
  const response = await api.post(`/details/${id}/end`, data);
  return response.data;
};

/** Who is detailed INTO this group right now — the roster's "Detailed in" section. */
export const getGroupDetails = async (groupId, { includeDescendants } = {}) => {
  const response = await api.get(`/org-groups/${groupId}/details`, {
    params: { includeDescendants },
  });
  return response.data;
};

/**
 * The paged, admin-scoped detail list. Returns the raw Spring `Page` — a caller rendering a
 * worklist needs `totalPages`/`last`, the same reason `getAgenciesPage` does.
 */
export const listDetails = async (params = {}) => {
  const response = await api.get('/details', { params });
  return response.data;
};

/**
 * The agency's detail policy, readable by anyone in the tenant.
 *
 * Deliberately not the agency-admin path: the detail form has to render "max 30 days" and
 * default its routing toggle for a group administrator who may never open the agency screen.
 */
export const getOrgPolicy = async () => {
  const response = await api.get('/org-policy');
  return response.data;
};

/**
 * Change the policy. AGENCY_ADMIN only, and a PATCH: `null` leaves a field alone, and
 * `detailMaxDays: 0` is the documented "no limit" sentinel — the column is nullable, so a JSON
 * null cannot be told apart from "not supplied".
 */
export const updateOrgPolicy = async (data) => {
  const response = await api.patch('/agency-admin/org-policy', data);
  return response.data;
};

// ---------------------------------------------------------------------------
// Organization structure templates (ORG-29, #324) — the third dual-mode catalog.
//
// The six below serve the BOUND schema: under a master claim they are the catalog
// originals an INITIAL_ADMIN authors, under a tenant claim that agency's copies and
// locals. The two admin ones are the master surface — publishOrgStructureTemplate
// fans a template out to every agency, and getMasterOrgStructureTemplates reads the
// originals under ANY binding (which GET /org-structure-templates cannot, by design).
//
// The fan-out is named publish… rather than distribute…: #274 drew that distinction
// because base forms have BOTH a status transition and a fan-out on paths that read
// alike. A structure template has no publish state at all, so there is only one
// "publish" here and nothing to disambiguate — the same reasoning that leaves
// publishRankStructure named as it is.
// ---------------------------------------------------------------------------

export const getOrgStructureTemplates = async () => {
  const response = await api.get('/org-structure-templates');
  return response.data;
};

export const getOrgStructureTemplate = async (id) => {
  const response = await api.get(`/org-structure-templates/${id}`);
  return response.data;
};

export const createOrgStructureTemplate = async (payload) => {
  const response = await api.post('/org-structure-templates', payload);
  return response.data;
};

export const updateOrgStructureTemplate = async (id, payload) => {
  const response = await api.put(`/org-structure-templates/${id}`, payload);
  return response.data;
};

export const deleteOrgStructureTemplate = async (id) => {
  const response = await api.delete(`/org-structure-templates/${id}`);
  return response.data;
};

export const cloneOrgStructureTemplate = async (id, payload) => {
  const response = await api.post(`/org-structure-templates/${id}/clone`, payload);
  return response.data;
};

export const publishOrgStructureTemplate = async (id) => {
  const response = await api.post(`/admin/catalog/org-structure-templates/${id}/publish`);
  return response.data;
};

export const getMasterOrgStructureTemplates = async () => {
  const response = await api.get('/admin/catalog/org-structure-templates');
  return response.data;
};

// ORG-30 (#325): instantiate a template as real groups. `id` is this tenant's COPY id —
// what getOrgStructureTemplates() returns under a tenant claim — never a master original's.
// A master id only ever reaches the server through createAgency's orgStructureTemplateId,
// which the backend resolves to the tenant's copy itself.
//
// 201 with the agency's whole tree (the apply refuses a non-empty organization, so "what was
// created" and "the whole tree" are the same set). 403 TENANT_NOT_SELECTED under a master
// claim, which api.js's existing interceptor already recovers from.
export const applyOrgStructureTemplate = async (id, payload) => {
  const response = await api.post(`/org-structure-templates/${id}/apply`, payload ?? {});
  return response.data;
};

// ---------------------------------------------------------------------------
// The document page (DOC-07, #455) over DOC-06's metadata endpoints (#454).
//
// DocumentMetadataController, not DocumentController: these answer RFC 9457 bodies that
// serverMessage() reads, where the older /documents endpoints still answer the legacy
// {error} shape (#257). The details and move writes answer the refreshed detail; the link
// writes do not (an add answers the new link, a removal nothing). Writes are
// hasRole('GROUP_ADMIN') and the service re-checks Can Edit; the detail's `canEdit` is the
// one flag the page gates on.
// ---------------------------------------------------------------------------

// 404 unknown id · 403 no view · 409 DOCUMENT_ARCHIVED below AGENCY_ADMIN.
export const getDocumentDetail = async (id) => {
  const response = await api.get(`/documents/${id}/detail`);
  return response.data;
};

// Replaced whole: {name, effectiveDate, description, tagIds}. tagIds is the COMPLETE set;
// [] clears it. The folder moves through moveDocument and the minimum rank is not written
// here (owner ruling 2026-09-23; the write is #497).
export const updateDocumentDetails = async (id, payload) => {
  const response = await api.put(`/documents/${id}/details`, payload);
  return response.data;
};

// folderId null = the top level. Needs Can Edit on the document and on both folders.
// #502: a move that changes who can read the document answers 409 DOCUMENT_MOVE_CHANGES_ACCESS
// with {lostAccess, gainedAccess, documentCount} until it is re-sent with
// acknowledgeAccessChanges: true.
export const moveDocument = async (id, folderId, acknowledgeAccessChanges = false) => {
  const response = await api.post(`/documents/${id}/move`, { folderId, acknowledgeAccessChanges });
  return response.data;
};

// {label, url | linkedDocumentId} — exactly one target; a url must be http/https.
// 201 with the new link ({id, label, url, linkedDocumentId, linkedDocumentName, available}),
// NOT the detail — re-read the detail for the list.
export const addDocumentLink = async (id, payload) => {
  const response = await api.post(`/documents/${id}/links`, payload);
  return response.data;
};

export const removeDocumentLink = async (id, linkId) => {
  await api.delete(`/documents/${id}/links/${linkId}`);
};

// The agency's tag vocabulary, [{id, name}].
export const getDocumentTags = async () => {
  const response = await api.get('/document-tags');
  return response.data;
};

// A name that already exists, in any spelling the server folds together, answers the
// existing tag (200) rather than a 409, so inline creation of a known name just selects it.
export const createDocumentTag = async (name) => {
  const response = await api.post('/document-tags', { name });
  return response.data;
};

// The folders this reader may view, flat and pre-order: [{id, parentId, name, depth, …}].
// A viewable folder whose parent is hidden is answered at depth 0 (DOC-03).
export const getDocumentFolders = async () => {
  const response = await api.get('/document-folders');
  return response.data;
};

// The folder writes (DOC-02; the browser is DOC-05 #453). All four are GROUP_ADMIN plus Can Edit,
// and each folder row's `canEdit` says whether the caller has it. Create, rename and move answer
// the folder; delete answers nothing. A move that changes who can read a document beneath the
// folder answers 409 DOCUMENT_MOVE_CHANGES_ACCESS until re-sent acknowledged (#502), and a delete
// of a folder with anything in it answers 409 FOLDER_NOT_EMPTY naming the counts.
export const createDocumentFolder = async (name, parentId) => {
  const response = await api.post('/document-folders', { name, parentId: parentId || null });
  return response.data;
};

export const renameDocumentFolder = async (id, name) => {
  const response = await api.put(`/document-folders/${id}`, { name });
  return response.data;
};

export const moveDocumentFolder = async (id, parentId, acknowledgeAccessChanges = false) => {
  const response = await api.post(`/document-folders/${id}/move`,
    { parentId: parentId || null, acknowledgeAccessChanges });
  return response.data;
};

export const deleteDocumentFolder = async (id) => {
  await api.delete(`/document-folders/${id}`);
};

// Whether the caller may create a folder or file a document at the top level: {canEdit}. The top
// level is the agency root, which has no folder row for the tree to carry it on (DOC-05, #453).
export const getDocumentFolderTopLevel = async () => {
  const response = await api.get('/document-folders/top-level');
  return response.data;
};

// Permission lists (DOC-03 #451; the Security tab and the folder dialog are DOC-13 #461), one
// read / add / remove per scope: a document, a folder, the top level. All GROUP_ADMIN, then Can
// View to read and Can Edit to change, checked by the server. The read answers
// DocumentPermissionsResponse {canEdit, entries}; an add answers the ONE entry it wrote (201 new,
// 200 when it replaced that principal's entry on the same scope), never the list; a remove
// answers 204. An inherited entry is removed only through the scope it sits on.
export const getDocumentPermissions = async (documentId) => {
  const response = await api.get(`/documents/${documentId}/permissions`);
  return response.data;
};

export const addDocumentPermission = async (documentId, request) => {
  const response = await api.post(`/documents/${documentId}/permissions`, request);
  return response.data;
};

export const removeDocumentPermission = async (documentId, entryId) => {
  await api.delete(`/documents/${documentId}/permissions/${entryId}`);
};

export const getDocumentFolderPermissions = async (folderId) => {
  const response = await api.get(`/document-folders/${folderId}/permissions`);
  return response.data;
};

export const addDocumentFolderPermission = async (folderId, request) => {
  const response = await api.post(`/document-folders/${folderId}/permissions`, request);
  return response.data;
};

export const removeDocumentFolderPermission = async (folderId, entryId) => {
  await api.delete(`/document-folders/${folderId}/permissions/${entryId}`);
};

export const getRootDocumentPermissions = async () => {
  const response = await api.get('/document-permissions/root');
  return response.data;
};

export const addRootDocumentPermission = async (request) => {
  const response = await api.post('/document-permissions/root', request);
  return response.data;
};

export const removeRootDocumentPermission = async (entryId) => {
  await api.delete(`/document-permissions/root/${entryId}`);
};

// A document's activity journal (DOC-08 #456; the Activity tab is DOC-12 #460): GROUP_ADMIN plus
// Can Edit. Answers a Spring Page of DocumentActivityResponse. `activity` is a list of kinds and
// is sent repeated (activity=A&activity=B), which is what Spring binds to a Set — axios's own
// serializer would write activity[]=A. Absent keys are not sent; an empty `activity` would mean
// every kind, so the caller must not ask at all when no kind can match.
export const getDocumentActivity = async (id, { activity, ...rest } = {}) => {
  const params = new URLSearchParams();
  (activity ?? []).forEach((kind) => params.append('activity', kind));
  Object.entries(rest).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, value);
  });
  const qs = params.toString();
  const response = await api.get(`/documents/${id}/activity${qs ? `?${qs}` : ''}`);
  return response.data;
};

// Document revisions (DOC-09 #457, DOC-10 #458; the Revisions tab is DOC-11 #459). Every one is
// GROUP_ADMIN plus Can Edit on the document. Every write answers ONE DocumentRevisionResponse
// ({id, status, revisionDate, comments, signature settings, currentFile, files[] newest first}),
// never the document's detail, except the discard, which answers nothing (204).

// Every revision, newest first. A document the previous release left without one is given its
// revision by this read.
export const getDocumentRevisions = async (id) => {
  const response = await api.get(`/documents/${id}/revisions`);
  return response.data;
};

const revisionFileForm = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

// A new draft from an uploaded file (201). 409 DRAFT_EXISTS while the document has one.
export const createDocumentDraft = async (id, file) => {
  const response = await api.post(`/documents/${id}/revisions`, revisionFileForm(file), {
    // Let the browser set the multipart boundary (same as uploadDocument).
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

// Duplicate to drafts, from a revision of any status (201). 409 DRAFT_EXISTS while a draft exists.
export const duplicateDocumentRevision = async (id, revisionId) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/duplicate`);
  return response.data;
};

// Replaced whole: {revisionDate, comments, signatureTimerMinutes, signaturesDueKind,
// signaturesDueDays, signaturesDueDate, workflowDueDate}. A draft or the published revision;
// an archived one is 409 REVISION_ARCHIVED.
export const updateDocumentRevision = async (id, revisionId, payload) => {
  const response = await api.put(`/documents/${id}/revisions/${revisionId}`, payload);
  return response.data;
};

// Attach a test to a DRAFT, or detach it with null (DOC-26 #474, attached in DOC-28 #476): {testId}.
// Its own endpoint because the settings PUT above replaces the set whole. 409 REVISION_NOT_DRAFT on a
// published or archived revision, TEST_RETIRED for a retired test; unchanged is a no-op. Answers the revision.
export const setDocumentRevisionTest = async (id, revisionId, testId) => {
  const response = await api.put(`/documents/${id}/revisions/${revisionId}/test`, { testId });
  return response.data;
};

// DOC-29 (#477): a draft's recall cards, `[{prompt, answer}]` in order, replacing the set whole; `[]`
// removes them. A draft only (REVISION_NOT_DRAFT), never beside a test (REQUIREMENT_EXCLUSIVE, either way
// round); a 400's detail names the card ("Card 2: write the prompt."). → the revision.
export const setDocumentRevisionRecallCards = async (id, revisionId, cards) => {
  const response = await api.put(`/documents/${id}/revisions/${revisionId}/recall-cards`, { cards });
  return response.data;
};

// 204. A draft only (REVISION_NOT_DRAFT), and never the document's only revision
// (DRAFT_IS_ONLY_REVISION).
export const discardDocumentDraft = async (id, revisionId) => {
  await api.delete(`/documents/${id}/revisions/${revisionId}`);
};

// Publishes the draft and archives the revision it replaces. 409 REVISION_NOT_DRAFT.
export const publishDocumentRevision = async (id, revisionId) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/publish`);
  return response.data;
};

// Takes the published revision out of circulation, with no replacement. 409 REVISION_NOT_PUBLISHED.
export const archiveDocumentRevision = async (id, revisionId) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/archive`);
  return response.data;
};

// A new file version on a draft, made current (201). 409 REVISION_NOT_DRAFT on any other revision.
export const addDocumentRevisionFile = async (id, revisionId, file) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/files`, revisionFileForm(file), {
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

export const makeDocumentRevisionFileCurrent = async (id, revisionId, fileId) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/files/${fileId}/make-current`);
  return response.data;
};

// 200 with the revision, not 204. 409 FILE_IS_CURRENT for the current version.
export const removeDocumentRevisionFile = async (id, revisionId, fileId) => {
  const response = await api.delete(`/documents/${id}/revisions/${revisionId}/files/${fileId}`);
  return response.data;
};

// Duplicate to drafts with this version current, from any revision (201). 409 DRAFT_EXISTS.
export const copyDocumentRevisionFileToDraft = async (id, revisionId, fileId) => {
  const response = await api.post(`/documents/${id}/revisions/${revisionId}/files/${fileId}/copy-to-draft`);
  return response.data;
};

// One file version's bytes and its text rendition, for DocumentViewer's `revisionFile` source.
// An editor may open any version; any other reader only the published revision's current file.
export const fetchDocumentRevisionFileContentBlob = async (id, revisionId, fileId, { signal } = {}) => {
  const response = await api.get(
    `/documents/${id}/revisions/${revisionId}/files/${fileId}/content`,
    { responseType: 'blob', signal }
  );
  return response.data;
};

export const getDocumentRevisionFileTextPreview = async (id, revisionId, fileId, { signal } = {}) => {
  const response = await api.get(
    `/documents/${id}/revisions/${revisionId}/files/${fileId}/preview-text`,
    { signal }
  );
  return response.data;
};

// A document's review schedule and review log (DOC-20, #468; PRD-12 §7). All four are GROUP_ADMIN
// plus Can Edit on the document except the due list, which any reader may ask for (it answers
// only what they may edit, so nothing for an officer).
//
// {mode: 'EVERY' | 'NEVER', interval, unit: 'DAYS' | 'MONTHS' | 'YEARS', anchorDate}. Answers the
// refreshed DocumentDetailResponse, whose `review` carries the derived dates. NEVER keeps the
// stored interval, unit and date on the server, so turning the schedule back on restores them.
export const updateDocumentReviewSchedule = async (id, payload) => {
  const response = await api.put(`/documents/${id}/review-schedule`, payload);
  return response.data;
};

// The review log, newest review first: [{id, reviewDate, details, createdBy, createdAt}].
export const getDocumentReviews = async (id) => {
  const response = await api.get(`/documents/${id}/reviews`);
  return response.data;
};

// Log New Review: {reviewDate, details}. Answers 201 with the ONE row it wrote, not the detail,
// so the caller re-reads the detail for the moved dates.
export const logDocumentReview = async (id, payload) => {
  const response = await api.post(`/documents/${id}/reviews`, payload);
  return response.data;
};

// The due-for-review list: a Spring Page of the document list's rows, each with
// `nextReviewDate`, most overdue first, across every folder. No screen reads it yet (owner
// ruling 2026-09-24: the API and the page's badge); DOC-24 (#472)'s My review tasks may.
export const getDocumentsDueForReview = async ({ page = 0, size = 20 } = {}) => {
  const response = await api.get('/documents', { params: { dueForReview: true, page, size } });
  return response.data;
};

// ============================================================================
// Standing assignments (DOC-14 #462 backend, DOC-16 #464 tab; PRD-12 §6, A20, DM-7). Every
// endpoint is GROUP_ADMIN plus Can Edit on the document, D3-scoped. Each write answers the ONE
// assignment it touched, with `outcome` = {obligated, alreadyObligated, skippedRank, withdrawn,
// published}; a toggle that changed nothing (already in that state) answers `outcome: null`.
// ============================================================================

// DM-7's view filter: 'requested' (active, the default), 'notRequested' (suspended) or 'all'.
// Oldest first. The kind and the name are filtered by the caller, not the server.
export const getDocumentAssignments = async (documentId, filter = 'requested') => {
  const response = await api.get(`/documents/${documentId}/assignments`, { params: { filter } });
  return response.data;
};

// {principalKind: 'USER', userId} or {principalKind: 'ORG_GROUP', orgGroupId, includeDescendants}.
// 201 with the one row; 409 ASSIGNMENT_EXISTS if that person or group is already assigned.
export const addDocumentAssignment = async (documentId, payload) => {
  const response = await api.post(`/documents/${documentId}/assignments`, payload);
  return response.data;
};

export const suspendDocumentAssignment = async (documentId, assignmentId) => {
  const response = await api.post(`/documents/${documentId}/assignments/${assignmentId}/suspend`);
  return response.data;
};

export const resumeDocumentAssignment = async (documentId, assignmentId) => {
  const response = await api.post(`/documents/${documentId}/assignments/${assignmentId}/resume`);
  return response.data;
};

// ============================================================================
// Signatures of one revision (DOC-17 #465 backend, DOC-18 #466 tab; PRD-12 §11, A21–A22). All
// GROUP_ADMIN plus Can View on the document, narrowed to the caller's admin subtree (people placed
// there today). Rows, selection and reminders are keyed by `acknowledgementId`, never the person.
// ============================================================================

// A Spring Page of {acknowledgementId, userId, username, userActive, status, withdrawnReason,
// dueDate, signedAt, lastRemindedAt, recordedGroupName}. `status` is a Set on the server, so it is
// REPEATED (`status=A&status=B`, what Spring binds; axios would write `status[]=`); nothing is sent
// that was not given. `sort` is USERNAME / DUE_DATE / SIGNED_AT / STATUS; at most 100 a page.
export const getDocumentSignatures = async (documentId, revisionId, { status, ...rest } = {}) => {
  const params = new URLSearchParams();
  (status ?? []).forEach((value) => params.append('status', value));
  Object.entries(rest).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, value);
  });
  const qs = params.toString();
  const response = await api.get(`/documents/${documentId}/revisions/${revisionId}/signatures${qs ? `?${qs}` : ''}`);
  return response.data;
};

// {documentName, revisionDate, revisionStatus, owed, signed, needed, overdue, withdrawn, byGroup[]}.
// `owed` = needed + overdue + signed; `withdrawn` sits beside it, never inside it.
export const getDocumentSignatureSummary = async (documentId, revisionId) => {
  const response = await api.get(`/documents/${documentId}/revisions/${revisionId}/signature-summary`);
  return response.data;
};

// 1–500 ids → {reminded, notOwed, notInScope}. Notifies each person still owing, and stamps the row.
export const remindDocumentSignatures = async (documentId, revisionId, acknowledgementIds) => {
  const response = await api.post(`/documents/${documentId}/revisions/${revisionId}/signatures/remind`, {
    acknowledgementIds,
  });
  return response.data;
};

// ============================================================================
// Workflow templates (DOC-21 #469 backend, DOC-22 #470 builder; PRD-12 §8). All GROUP_ADMIN and up.
// A template is written by D3 (owner ruling 2026-09-25): `canEdit` on the one-template read says
// whether the reader may change or retire it; the list carries none.
// ============================================================================

// Live templates by name, each with its step count; `includeRetired` adds the retired ones.
export const getWorkflowTemplates = async ({ includeRetired = false } = {}) => {
  const response = await api.get('/document-workflow-templates', { params: includeRetired ? { includeRetired: true } : {} });
  return response.data;
};

// One template, whole (a retired one too).
export const getWorkflowTemplate = async (id) => {
  const response = await api.get(`/document-workflow-templates/${id}`);
  return response.data;
};

export const createWorkflowTemplate = async (payload) => {
  const response = await api.post('/document-workflow-templates', payload);
  return response.data;
};

// Replaces the name, the switch and the whole step list: a step's id does not survive it.
export const updateWorkflowTemplate = async (id, payload) => {
  const response = await api.put(`/document-workflow-templates/${id}`, payload);
  return response.data;
};

// There is no delete (405 USE_RETIRE) and no way back from here: DOC-21 built no reactivate.
export const retireWorkflowTemplate = async (id) => {
  const response = await api.post(`/document-workflow-templates/${id}/retire`);
  return response.data;
};

// ============================================================================
// The test library (DOC-26 #474 backend, DOC-27 #475 screen; PRD-12 §9). All GROUP_ADMIN and up,
// and the authors' surface: the one-test read carries the answer key (#550, ruled A 2026-10-02).
// A signer takes a test through the acknowledgement, never through these.
// ============================================================================

// Live tests by name, each with its question count and whether anyone has taken it; no questions.
// `includeRetired` adds the retired ones, which no picker offers.
export const getDocumentTests = async ({ includeRetired = false } = {}) => {
  const response = await api.get('/document-tests', { params: includeRetired ? { includeRetired: true } : {} });
  return response.data;
};

// One test, whole, with its answer key and `attempted` (a retired one too).
export const getDocumentTest = async (id) => {
  const response = await api.get(`/document-tests/${id}`);
  return response.data;
};

export const createDocumentTest = async (payload) => {
  const response = await api.post('/document-tests', payload);
  return response.data;
};

// Replaces the name, the pass mark and every question; once anyone has taken the test, only the
// name may change (409 TEST_HAS_ATTEMPTS otherwise).
export const updateDocumentTest = async (id, payload) => {
  const response = await api.put(`/document-tests/${id}`, payload);
  return response.data;
};

// There is no delete (405 USE_RETIRE) and no way back: DOC-26 built no reactivate.
export const retireDocumentTest = async (id) => {
  const response = await api.post(`/document-tests/${id}/retire`);
  return response.data;
};

// ============================================================================
// Review workflows (DOC-23 #471 engine, DOC-24 #472 screens; PRD-12 §8). The first three are an
// editor's (GROUP_ADMIN + Can Edit); the last three are the participant's own, any role — someone
// else's task is a 404.
// ============================================================================

// Every review of the document, newest first, each with its steps, participants and tasks.
// `revisionId` is null once the reviewed draft was discarded.
export const getDocumentWorkflows = async (documentId) => {
  const response = await api.get(`/documents/${documentId}/workflows`);
  return response.data;
};

// {templateId, name?, dueDate?} → 201 with the workflow. The name defaults to the template's and the
// due date to the revision's Workflow Due Date. Refusals carry a `code` (WORKFLOW_IN_PROGRESS,
// REVISION_NOT_DRAFT, TEMPLATE_RETIRED, STEP_REACHES_NOBODY, DOCUMENT_ARCHIVED).
export const startDocumentWorkflow = async (documentId, revisionId, payload) => {
  const response = await api.post(`/documents/${documentId}/revisions/${revisionId}/workflows`, payload);
  return response.data;
};

export const cancelDocumentWorkflow = async (workflowId) => {
  const response = await api.post(`/document-workflows/${workflowId}/cancel`);
  return response.data;
};

// The caller's open tasks, the soonest step due first.
export const getMyReviewTasks = async () => {
  const response = await api.get('/document-workflows/my-tasks');
  return response.data;
};

// One of the caller's own tasks, open or not. Its file fields are null once it is not open.
export const getReviewTask = async (taskId) => {
  const response = await api.get(`/document-workflows/tasks/${taskId}`);
  return response.data;
};

// {decision: 'APPROVED' | 'REJECTED', comment} → the task as it now stands.
export const decideReviewTask = async (taskId, payload) => {
  const response = await api.post(`/document-workflows/tasks/${taskId}/decision`, payload);
  return response.data;
};
